import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect } from 'wouter';
import { ActionIcon, Box, Button, Group, SegmentedControl, Tabs, Text, Tooltip } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconArrowsMaximize, IconArrowsMinimize, IconCoin, IconFocus2, IconLayoutColumns, IconMinus, IconPlus, IconUserEdit } from '@tabler/icons-react';
import { SpeakerNotesDrawer } from '../speakers/SpeakerNotesDrawer';
import { ResolutionBar } from './ResolutionBar';
import { rowsForSheet, sheetsFor, sheetTitle, type FlowCell, type LayoutMode, type SideIndex } from '@flow/core';
import { useDeps, useUseCases } from '../../app/services';
import { PageShell } from '../../ui/PageShell';
import { useKnowledge } from '../knowledge/KnowledgeDrawer';
import { useKeymap } from '../../ui/useKeymap';
import { K } from '../../ui/Kbd';
import { enterFullscreen, exitFullscreen, setWakeLock } from '../../infra/browser';
import { FlowGrid } from '../flow/FlowGrid';
import { TimerStrip } from '../timer/TimerStrip';
import { useTimer } from '../timer/useTimer';
import { CoinFlipModal } from '../today/CoinFlipModal';
import { sidesSummary, roundHref } from '../tournaments/TournamentPage';
import { paths, useQueryState, type RoundTab } from '../../app/routes';
import { DecidePanel } from '../decide/DecidePanel';
import { BallotPanel } from '../decide/BallotPanel';
import type { Round } from '@flow/core';

/**
 * /t/:tid/r/:rid/:tab (flow | decide | ballot). Everything else open on the page is in the query, so any view
 * can be linked to: ?sheet=<sheet id> · view=focus · panel=notes · paper=1 · modal=sides · cell=<row id>~<speech id>
 * (`-` for "no row": the top of that speech's column).
 */
export const RoundPage = ({ tid, rid, tab }: { tid: string; rid: string; tab: RoundTab | null }) => {
  const deps = useDeps();
  const round = useLiveQuery(async () => (await deps.rounds.get(rid)) ?? null, [rid]);
  const tournament = useLiveQuery(async () => (await deps.tournaments.get(tid)) ?? null, [tid]);
  if (round === undefined || tournament === undefined) return null;
  if (round === null) {
    return (
      <PageShell title="Round not found" crumbs={[{ label: 'Tournaments', href: paths.dashboard }, ...(tournament ? [{ label: tournament.name, href: paths.tournament(tid) }] : [])]}>
        <Text mb="md">This round isn't on this device. It may have been deleted.</Text>
        <Button component={Link} href={tournament ? paths.tournament(tid) : paths.dashboard}>Back</Button>
      </PageShell>
    );
  }
  // A wrong or missing piece of the path (old link, no tab): go to the round's proper address.
  if (round.tournamentId !== tid || !tab) return <Redirect to={tab && round.tournamentId !== tid ? paths.round(round.tournamentId, round.id, tab) + location.search : roundHref(round)} replace />;
  return <RoundView round={round} tab={tab} tournamentName={tournament?.name ?? 'Tournament'} />;
};

const parseCell = (v: string | null) => {
  const m = v ? /^(.+)~(.+)$/.exec(v) : null;
  return m ? { rowId: m[1] === '-' ? null : m[1]!, segId: m[2]! } : null;
};

const RoundView = ({ round, tab, tournamentName }: { round: Round; tab: RoundTab; tournamentName: string }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const t = useTimer(round);
  const url = useQueryState();
  // Switching tabs keeps the sheet and view; things open on one tab (a cell, a dialog, paper mode) don't carry over.
  const setTab = useCallback((next: RoundTab, extra: Record<string, string | null> = {}) => {
    const keep = new URLSearchParams(window.location.search);
    const params: Record<string, string | null> = { sheet: keep.get('sheet'), view: keep.get('view'), panel: keep.get('panel'), ...extra };
    url.navigate(paths.round(round.tournamentId, round.id, next, params));
  }, [url.navigate, round.tournamentId, round.id]);
  const paper = tab === 'flow' && url.get('paper') === '1';
  const setPaper = (on: boolean) => (on ? (tab === 'flow' ? url.set({ paper: '1' }) : setTab('flow', { paper: '1' })) : url.close('paper'));
  const mode: LayoutMode = url.get('view') === 'focus' ? 'focus' : 'fit';
  const setMode = (m: LayoutMode) => url.set({ view: m === 'focus' ? 'focus' : null }, { replace: true });
  const flip = url.get('modal') === 'sides';
  const setFlip = (on: boolean) => (on ? url.set({ modal: 'sides' }) : url.close('modal'));
  const notesOpen = url.get('panel') === 'notes';
  const setNotesOpen = (on: boolean) => (on ? url.set({ panel: 'notes' }) : url.close('panel'));
  // A request to show a spot on the flow: from the argument map, or a link with ?cell=.
  const [jump, setJump] = useState<{ rowId: string | null; segId: string; nonce: number } | null>(() => {
    const c = parseCell(url.get('cell'));
    return c ? { ...c, nonce: Date.now() } : null;
  });
  const paperRef = useRef<HTMLDivElement>(null);
  const settings = useLiveQuery(() => deps.settings.get(), []);
  const fontScale = settings?.flowFontScale ?? 1;

  const rows = useLiveQuery(() => deps.flow.rows(round.id), [round.id]) ?? [];
  const cellList = useLiveQuery(() => deps.flow.cells(round.id), [round.id]) ?? [];
  const cells = useMemo(() => new Map<string, FlowCell>(cellList.map((c) => [c.id, c])), [cellList]);
  const layout = settings?.flowLayout ?? 'single';
  const sheets = useMemo(() => sheetsFor(round.format, layout, rows), [round.format, layout, rows]);
  const sheetIdx = Math.max(0, sheets.findIndex((s) => s.id === url.get('sheet')));
  const setSheetIdx = (i: number) => url.set({ sheet: i === 0 ? null : sheets[i]?.id ?? null }, { replace: true });
  const sheet = sheets[Math.min(sheetIdx, sheets.length - 1)]!;
  const sheetRows = useMemo(() => rowsForSheet(round.format, sheet, rows), [round.format, sheet, rows]);

  useEffect(() => {
    if (round.status === 'scheduled') void uc.setRoundStatus(round.id, 'inRound');
  }, [round.id]);
  // Settings load a moment after the round; add starter rows once the layout is known (and if it changes).
  const loadedLayout = settings?.flowLayout;
  useEffect(() => {
    if (loadedLayout) void uc.ensureStarterRows(round.id, round.format, loadedLayout);
  }, [round.id, loadedLayout]);

  // Paper mode: full screen when the browser allows it, CSS full-bleed otherwise. Keep the screen awake.
  useEffect(() => {
    if (paper) {
      if (paperRef.current) void enterFullscreen(paperRef.current);
      void setWakeLock(true);
    } else {
      void exitFullscreen();
    }
  }, [paper]);

  const setFont = (d: number) => settings && void deps.settings.put({ ...settings, flowFontScale: Math.min(1.8, Math.max(0.7, Math.round((fontScale + d) * 10) / 10)) });
  const step = (dir: 1 | -1) => setSheetIdx((sheetIdx + dir + sheets.length) % sheets.length);

  useKeymap([
    { code: 'Space', alt: true, run: () => void t.send({ t: 'toggle' }) },
    { code: 'Period', alt: true, run: () => void t.send({ t: 'done' }) },
    { code: 'Comma', alt: true, run: () => t.state && void t.send({ t: 'goto', index: Math.max(0, t.state.cursor - 1) }) },
    { code: 'Digit1', alt: true, run: () => round.format.prepSec > 0 && void t.send({ t: 'togglePrep', side: 0 as SideIndex }) },
    { code: 'Digit2', alt: true, run: () => round.format.prepSec > 0 && void t.send({ t: 'togglePrep', side: 1 as SideIndex }) },
    { code: 'BracketLeft', alt: true, run: () => void t.send({ t: 'adjust', clock: t.state?.running === 'prep0' ? 'prep0' : t.state?.running === 'prep1' ? 'prep1' : 'speech', deltaMs: -15_000 }) },
    { code: 'BracketRight', alt: true, run: () => void t.send({ t: 'adjust', clock: t.state?.running === 'prep0' ? 'prep0' : t.state?.running === 'prep1' ? 'prep1' : 'speech', deltaMs: 15_000 }) },
    { code: 'KeyF', alt: true, run: () => setPaper(!paper) },
    { code: 'KeyZ', alt: true, run: () => setMode(mode === 'fit' ? 'focus' : 'fit') },
    { code: 'KeyN', alt: true, run: () => setNotesOpen(!notesOpen) },
    { code: 'Equal', alt: true, run: () => setFont(0.1) },
    { code: 'Minus', alt: true, run: () => setFont(-0.1) },
    { code: 'KeyS', alt: true, run: () => step(1) },
  ]);

  const timerSeg = t.segment?.id ?? null;
  // The reference drawer's cheatsheet follows the event being judged.
  const knowledge = useKnowledge();
  useEffect(() => knowledge.setEvent(round.format.event ?? 'pf'), [round.format.event, knowledge]);
  // Per-case sheets follow the timer the way PF is flowed: a constructive goes to its own case's sheet, a rebuttal to
  // the case it attacks; other speeches stay put unless they aren't on this sheet. Only on timer changes, so
  // switching sheets by hand still sticks.
  useEffect(() => {
    const seg = round.format.segments.find((s) => s.id === timerSeg);
    if (!seg || sheets.length < 2) return;
    const other = (slot: 'first' | 'second') => (slot === 'first' ? 'second' : 'first');
    const owner = seg.by === 'both' ? null : seg.role === 'constructive' ? seg.by : seg.role === 'rebuttal' ? other(seg.by) : null;
    const i = owner ? sheets.findIndex((s) => s.owner === owner) : sheet.segmentIds.includes(seg.id) ? -1 : sheets.findIndex((s) => s.segmentIds.includes(seg.id));
    if (i >= 0) setSheetIdx(i);
  }, [timerSeg]);
  const sheetTabs = sheets.length < 2 ? null : (
    <SegmentedControl
      size="xs"
      value={sheet.id}
      onChange={(v) => setSheetIdx(sheets.findIndex((s) => s.id === v))}
      // The case speech in the label ("Pro case · C1") so it's clear where C1 and C2 live.
      data={sheets.map((s) => ({ value: s.id, label: `${sheetTitle(round, s.title)} · ${s.segmentIds[0]}` }))}
      data-testid="sheet-tabs"
    />
  );
  const layoutControls = (
    <Group gap={2} wrap="nowrap">
      <Tooltip label="Round and speaker notes (⌥N)">
        <ActionIcon variant={notesOpen ? 'light' : 'subtle'} onClick={() => setNotesOpen(!notesOpen)} aria-label="Speaker notes" data-testid="speaker-notes-toggle"><IconUserEdit size={16} /></ActionIcon>
      </Tooltip>
      <Tooltip label={mode === 'fit' ? 'Focus view: just the speech and what it answers (⌥Z)' : 'Show all columns (⌥Z)'}>
        <ActionIcon onClick={() => setMode(mode === 'fit' ? 'focus' : 'fit')} aria-label="Toggle focus view">{mode === 'fit' ? <IconFocus2 size={16} /> : <IconLayoutColumns size={16} />}</ActionIcon>
      </Tooltip>
      <Tooltip label="Smaller text (⌥−)"><ActionIcon onClick={() => setFont(-0.1)} aria-label="Smaller text"><IconMinus size={14} /></ActionIcon></Tooltip>
      <Tooltip label="Larger text (⌥=)"><ActionIcon onClick={() => setFont(0.1)} aria-label="Larger text"><IconPlus size={14} /></ActionIcon></Tooltip>
    </Group>
  );
  const grid = (
    <Box style={{ ['--flow-font-scale' as string]: fontScale, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <FlowGrid round={round} sheet={sheet} rows={sheetRows} cells={cells} timerSegmentId={timerSeg} mode={mode} onSheetStep={step} jumpTo={jump} />
    </Box>
  );

  const notes = <SpeakerNotesDrawer round={round} segment={t.segment} opened={notesOpen} onClose={() => setNotesOpen(false)} />;

  if (paper) {
    return (
      <div className="paper-frame" ref={paperRef} data-testid="paper-mode">
        {notes}
        <Group gap="xs" px={6} py={2} wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)' }}>
          {sheetTabs}
          {layoutControls}
          <Box style={{ flex: 1, minWidth: 0 }}><TimerStrip round={round} t={t} compact /></Box>
          <Tooltip label="Exit paper mode (⌥F)">
            <ActionIcon onClick={() => setPaper(false)} aria-label="Exit paper mode"><IconArrowsMinimize size={16} /></ActionIcon>
          </Tooltip>
        </Group>
        {grid}
      </div>
    );
  }

  return (
    <PageShell
      wide
      crumbs={[{ label: 'Tournaments', href: paths.dashboard }, { label: tournamentName, href: paths.tournament(round.tournamentId) }]}
      title={`${round.label}: ${round.teamA.code} v ${round.teamB.code}`}
      subtitle={`${round.room ? `Room ${round.room} · ` : ''}${sidesSummary(round)}`}
      actions={
        <>
          <Tooltip label="Coin flip / sides"><ActionIcon size="lg" onClick={() => setFlip(true)} aria-label="Coin flip"><IconCoin size={18} /></ActionIcon></Tooltip>
          <Tooltip label="Paper mode: full-screen flow (⌥F)">
            <ActionIcon size="lg" onClick={() => setPaper(true)} aria-label="Paper mode" data-testid="paper-toggle"><IconArrowsMaximize size={18} /></ActionIcon>
          </Tooltip>
        </>
      }
    >
      <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TimerStrip round={round} t={t} />
        <Tabs value={tab} onChange={(v) => v && v !== tab && setTab(v as RoundTab)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} keepMounted={false}>
          <Group justify="space-between" px="xs" wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)' }}>
            <Tabs.List style={{ borderBottom: 0, flexWrap: 'nowrap' }}>
              <Tabs.Tab value="flow">Flow</Tabs.Tab>
              <Tabs.Tab value="decide" data-testid="tab-decide">Decide</Tabs.Tab>
              <Tabs.Tab value="ballot" data-testid="tab-ballot">Ballot</Tabs.Tab>
            </Tabs.List>
            {tab === 'flow' && <Group gap="xs" wrap="nowrap">{sheetTabs}{layoutControls}</Group>}
          </Group>
          <ResolutionBar round={round} />
          <Tabs.Panel value="flow" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {round.teamASide === null && (
              <Group px="sm" py={4} gap="xs" style={{ background: 'var(--mantine-color-yellow-light)' }}>
                {round.format.order === 'coinFlip'
                  ? <Text size="xs">Sides aren't set, so columns show 1st/2nd speaker.</Text>
                  : <Text size="xs">Which team is {round.format.sideLabels[0]}? Set it so columns show team names.</Text>}
                <Button size="compact-xs" onClick={() => setFlip(true)} data-testid="set-sides">{round.format.order === 'coinFlip' ? 'Record coin flip' : 'Set sides'}</Button>
              </Group>
            )}
            {grid}
          </Tabs.Panel>
          <Tabs.Panel value="decide" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <DecidePanel
              round={round}
              rows={rows}
              cells={cellList}
              onDone={() => setTab('ballot')}
              onJump={(rowId, segId) => {
                // Show the sheet that has this row (per-case rounds), then the cell.
                const sheetId = rows.find((r) => r.id === rowId)?.sheetId;
                const i = sheets.findIndex((s) => (sheetId && s.id === sheetId) || (!sheetId && s.segmentIds.includes(segId)));
                setTab('flow', { sheet: i > 0 ? sheets[i]!.id : null, cell: `${rowId ?? '-'}~${segId}` });
                setJump({ rowId, segId, nonce: Date.now() });
              }}
            />
          </Tabs.Panel>
          <Tabs.Panel value="ballot" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <BallotPanel round={round} goDecide={() => setTab('decide')} />
          </Tabs.Panel>
        </Tabs>
        <Text size="xs" c="dimmed" px="sm" py={2} style={{ borderTop: '1px solid var(--grid-line)' }}>
          <K>⌥F</K> paper · <K>⌥Z</K> focus · <K>⌥S</K> switch sheet · <K>⌥Space</K> timer · <K>⌥N</K> notes · <K>⌥.</K> next speech · <K>⌥1</K>/<K>⌥2</K> prep · <K>⌘G</K> glossary
        </Text>
      </Box>
      {flip && <CoinFlipModal round={round} opened onClose={() => setFlip(false)} />}
      {notes}
    </PageShell>
  );
};
